import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAuctionState } from './AuctionStateContext';
import { getSimulatedData } from '../utils/simulatedData';
import contributionService from '../services/contributionService';
import tokenService from '../services/tokenService';
import priceService from '../services/priceService';
import priceCalculator from '../utils/priceCalculator';

// Create the context
const ContributionContext = createContext();

export const useContribution = () => {
    const context = useContext(ContributionContext);
    if (!context) {
        throw new Error('useContribution must be used within a ContributionProvider');
    }
    return context;
};

export const ContributionProvider = ({ children }) => {
    const wallet = useWallet();
    const { 
        currentDay, 
        getDayAllocation, 
        updateTotalSolRaised,
        auctionState
    } = useAuctionState();
    
    // User contributions state
    const [userContributions, setUserContributions] = useState({});
    const [projectedContributions, setProjectedContributions] = useState({});
    const [confirmedJinu, setConfirmedJinu] = useState(0);
    const [pendingJinu, setPendingJinu] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    
    // SOL price state
    const [solPrice, setSolPrice] = useState(null);
    const [solPriceLoading, setSolPriceLoading] = useState(false);
    
    // Fetch SOL price on component mount and periodically
    useEffect(() => {
        const fetchSolPrice = async () => {
            setSolPriceLoading(true);
            try {
                const price = await priceService.getSolanaPrice();
                setSolPrice(price);
            } catch (error) {
                console.error('Failed to fetch SOL price:', error);
            } finally {
                setSolPriceLoading(false);
            }
        };
        
        // Fetch immediately
        fetchSolPrice();
        
        // Set up interval to refresh price every 5 minutes
        const interval = setInterval(fetchSolPrice, 5 * 60 * 1000);
        
        // Clean up interval on unmount
        return () => clearInterval(interval);
    }, []);
    
    // Get day stats without projection
    const getDayStatsWithoutProjection = useCallback((day) => {
        const simData = getSimulatedData(day);
        const dayAllocation = getDayAllocation(day);
        const confirmedContributions = userContributions[day] || [];
        
        if (!dayAllocation) return null;
        
        // Calculate user's total contribution for this day
        const userContribution = confirmedContributions.reduce((acc, contrib) => acc + contrib.amount, 0);
        
        // Calculate total contributions for this day
        const totalDayContributions = dayAllocation.totalAmount || 0;
        
        // Calculate user's share percentage
        const sharePercentage = totalDayContributions > 0 
            ? (userContribution / totalDayContributions) * 100
            : 0;
        
        // Calculate JINU earned
        const dayJinuAllocation = dayAllocation.jinuAllocation || 0;
        const dayJinu = (dayJinuAllocation * sharePercentage) / 100;
        
        return {
            day,
            userContribution,
            totalContributions: totalDayContributions,
            sharePercentage,
            jinuAllocation: dayJinuAllocation,
            confirmedJinu: dayJinu,
            projectedJinu: 0,
            projectedSharePercentage: sharePercentage
        };
    }, [getDayAllocation, userContributions]);
    
    // Get day stats with projection
    const getDayStatsWithProjection = useCallback((day, projectedAmount = 0) => {
        const baseStats = getDayStatsWithoutProjection(day);
        if (!baseStats) return null;
        
        // If no projection, return base stats
        if (projectedAmount <= 0) return baseStats;
        
        // Calculate new share percentage with projection
        const newUserContribution = baseStats.userContribution + projectedAmount;
        const newTotalContributions = baseStats.totalContributions + projectedAmount;
        
        const newSharePercentage = newTotalContributions > 0 
            ? (newUserContribution / newTotalContributions) * 100
            : 0;
        
        // Calculate projected JINU
        const projectedJinu = (baseStats.jinuAllocation * newSharePercentage) / 100;
        const additionalJinu = projectedJinu - baseStats.confirmedJinu;
        
        return {
            ...baseStats,
            projectedContribution: projectedAmount,
            totalWithProjected: newTotalContributions,
            projectedSharePercentage: newSharePercentage,
            projectedJinu: additionalJinu
        };
    }, [getDayStatsWithoutProjection]);
    
    // Load user contributions when wallet changes
    useEffect(() => {
        if (wallet.publicKey) {
            loadUserContributions();
        } else {
            // Reset state when wallet disconnects
            setUserContributions({});
            setProjectedContributions({});
            setConfirmedJinu(0);
            setPendingJinu(0);
        }
    }, [wallet.publicKey]);
    
    // Load user contributions from service
    const loadUserContributions = useCallback(async () => {
        if (!wallet.publicKey) return;
        
        setIsLoading(true);
        try {
            // In production, this would call the smart contract
            // For now, we'll use local storage to simulate persistence
            const walletAddress = wallet.publicKey.toString();
            const storedContributions = localStorage.getItem(`contributions_${walletAddress}`);
            
            if (storedContributions) {
                const parsedContributions = JSON.parse(storedContributions);
                setUserContributions(parsedContributions);
                
                // Calculate confirmed JINU based on loaded contributions
                const confirmed = calculateConfirmedJinu(parsedContributions);
                setConfirmedJinu(confirmed);
            }
        } catch (error) {
            console.error("Error loading user contributions:", error);
        } finally {
            setIsLoading(false);
        }
    }, [wallet.publicKey]);
    
    // Add contribution to a specific day
    const addContribution = useCallback(async (day, amount, referrer = null) => {
        if (!wallet.publicKey) return;
        
        setIsLoading(true);
        try {
            // Get the current price for this contribution
            const currentStats = priceCalculator.calculate(auctionState.totalSolRaised || 4036);
            const entryPrice = currentStats.currentPrice;
            console.log('Adding contribution with entry price:', entryPrice);
            
            // Call the contribution service
            const tx = await contributionService.contribute(day, amount, referrer, entryPrice);
            
            // Update local state
            setUserContributions(prev => {
                const dayContributions = [...(prev[day] || [])];
                
                // Add new contribution with entry price
                dayContributions.push({
                    amount,
                    timestamp: Date.now(),
                    txId: tx,
                    entryPrice // Store the entry price with the contribution
                });
                
                return {
                    ...prev,
                    [day]: dayContributions
                };
            });
            
            // Update total SOL raised in auction state
            const newTotal = (auctionState.totalSolRaised || 0) + amount;
            updateTotalSolRaised(newTotal);
            
            return tx;
        } catch (error) {
            console.error('Failed to add contribution:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [wallet.publicKey, auctionState.totalSolRaised, updateTotalSolRaised]);
    
    // Add a projected contribution
    const addProjectedContribution = useCallback((day, amount) => {
        if (amount <= 0) return;
        
        // Update projected contributions
        const updatedProjections = {
            ...projectedContributions,
            [day]: amount
        };
        
        setProjectedContributions(updatedProjections);
        
        // Recalculate pending JINU
        const pending = calculatePendingJinu(updatedProjections);
        setPendingJinu(pending);
    }, [projectedContributions]);
    
    // Clear all projected contributions
    const clearProjectedContributions = useCallback(() => {
        setProjectedContributions({});
        setPendingJinu(0);
    }, []);
    
    // Calculate confirmed JINU based on user contributions
    const calculateConfirmedJinu = useCallback((contributions) => {
        if (!contributions) return 0;
        
        let totalJinu = 0;
        
        // Calculate JINU for each day with contributions
        Object.entries(contributions).forEach(([day, dayContributions]) => {
            day = parseInt(day);
            const dayAllocation = getDayAllocation(day);
            if (!dayAllocation) return;
            
            // Calculate user's total contribution for this day
            const userContribution = dayContributions.reduce((acc, contrib) => acc + contrib.amount, 0);
            if (userContribution <= 0) return;
            
            // Calculate user's share percentage
            const totalDayContributions = dayAllocation.totalAmount || 0;
            const sharePercentage = totalDayContributions > 0 
                ? (userContribution / totalDayContributions) * 100
                : 0;
            
            // Calculate JINU earned
            const dayJinuAllocation = dayAllocation.jinuAllocation || 0;
            const dayJinu = (dayJinuAllocation * sharePercentage) / 100;
            
            totalJinu += dayJinu;
        });
        
        return totalJinu;
    }, [getDayAllocation]);
    
    // Calculate pending JINU based on projected contributions
    const calculatePendingJinu = useCallback((projectedContribs) => {
        if (!projectedContribs) return 0;
        
        let totalPendingJinu = 0;
        
        // Calculate pending JINU for each day with projected contributions
        Object.entries(projectedContribs).forEach(([day, amount]) => {
            day = parseInt(day);
            if (amount <= 0) return;
            
            // Get day stats with projection
            const dayStats = getDayStatsWithProjection(day, amount);
            if (!dayStats) return;
            
            totalPendingJinu += dayStats.projectedJinu || 0;
        });
        
        return totalPendingJinu;
    }, [getDayStatsWithProjection]);
    
    // Get user stats
    const getUserStats = useCallback(() => {
        if (!wallet.publicKey) return null;
        
        const participatedDays = [];
        let totalConfirmedSol = 0;
        let totalPendingSol = 0;
        let totalConfirmedJinu = 0;
        let totalPendingJinu = 0;
        
        // Calculate stats for each day
        for (let day = 1; day <= currentDay; day++) {
            // Get confirmed contributions
            const dayContributions = userContributions[day] || [];
            
            if (dayContributions.length > 0) {
                // Calculate total confirmed SOL for this day
                const dayTotal = dayContributions.reduce((sum, contrib) => {
                    return sum + contrib.amount;
                }, 0);
                
                totalConfirmedSol += dayTotal;
                
                // Get day stats without projection
                const dayStats = getDayStatsWithoutProjection(day);
                if (dayStats) {
                    totalConfirmedJinu += dayStats.confirmedJinu;
                    participatedDays.push(day);
                }
            }
            
            // Add projected contributions
            const projectedAmount = projectedContributions[day] || 0;
            if (projectedAmount > 0) {
                totalPendingSol += projectedAmount;
                
                // Get day stats with projection
                const dayStats = getDayStatsWithProjection(day, projectedAmount);
                if (dayStats) {
                    totalPendingJinu += dayStats.projectedJinu;
                }
            }
        }
        
        // Calculate participation stats
        const daysParticipated = participatedDays.length;
        
        return {
            daysParticipated,
            participatedDays,
            totalConfirmedSol,
            totalPendingSol,
            totalSol: totalConfirmedSol + totalPendingSol,
            totalConfirmedJinu,
            totalPendingJinu,
            totalJinu: totalConfirmedJinu + totalPendingJinu
        };
    }, [
        wallet.publicKey, 
        currentDay, 
        userContributions, 
        projectedContributions, 
        getDayStatsWithoutProjection, 
        getDayStatsWithProjection
    ]);
    
    // Calculate share percentage
    const calculateSharePercentage = useCallback((confirmedContribs, projectedContribs) => {
        // Calculate total confirmed contributions
        const totalConfirmed = Object.values(confirmedContribs).reduce((acc, val) => acc + val, 0);
        
        // Calculate total projected contributions
        const totalProjected = Object.values(projectedContribs).reduce((acc, val) => acc + val, 0);
        
        // Calculate total SOL raised
        const totalSol = auctionState.totalSolRaised || 1;
        
        // Calculate share percentage
        return ((totalConfirmed + totalProjected) / totalSol) * 100;
    }, [auctionState.totalSolRaised]);

    // Convert SOL to USD
    const solToUsd = useCallback((solAmount) => {
        if (!solPrice) return null;
        return solAmount * solPrice;
    }, [solPrice]);
    
    // Convert USD to SOL
    const usdToSol = useCallback((usdAmount) => {
        if (!solPrice || solPrice === 0) return null;
        return usdAmount / solPrice;
    }, [solPrice]);
    
    // Format USD amount
    const formatUsd = useCallback((amount) => {
        return priceService.formatUsd(amount);
    }, []);
    
    // Format SOL amount
    const formatSol = useCallback((amount) => {
        return priceService.formatSol(amount);
    }, []);

    return (
        <ContributionContext.Provider value={{
            // State
            userContributions,
            projectedContributions,
            confirmedJinu,
            pendingJinu,
            isLoading,
            solPrice,
            solPriceLoading,
            
            // Methods
            addContribution,
            addProjectedContribution,
            clearProjectedContributions,
            getDayStatsWithProjection,
            getDayStatsWithoutProjection,
            getUserStats,
            solToUsd,
            usdToSol,
            formatUsd,
            formatSol
        }}>
            {children}
        </ContributionContext.Provider>
    );
};

export default ContributionContext;
